import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ClipboardCheck, ChevronRight, Check } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { fetchMyPrograms } from '../../lib/programs';
import type { AssignedProgram } from '../../types/program';

export function MyPrograms() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [programs, setPrograms] = useState<AssignedProgram[]>([]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const data = await fetchMyPrograms(user.id);
      setPrograms(data);
    } catch (err) {
      console.error('Error loading programs:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingSpinner message="Loading programs..." />;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="freud-heading-lg text-chocolate-900">My Programs</h1>
          <p className="text-sm text-chocolate-500">Programs assigned to you by your coach</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {programs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="freud-card-elevated p-12 text-center"
          >
            <div className="w-16 h-16 bg-cream-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ClipboardCheck className="w-8 h-8 text-chocolate-300" />
            </div>
            <h2 className="freud-heading-base text-chocolate-900 mb-2">No Programs Assigned</h2>
            <p className="text-sm text-chocolate-500">When your coach assigns you a program, it will appear here.</p>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            {programs.map((item, index) => {
              const pct = item.totalActivities > 0 ? Math.round((item.completedActivities / item.totalActivities) * 100) : 0;
              const isComplete = item.completedActivities === item.totalActivities && item.totalActivities > 0;

              return (
                <motion.div
                  key={item.assignmentId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    to={`/dashboard/programs/${item.assignmentId}`}
                    className="block freud-card-elevated p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="freud-heading-base text-chocolate-900 truncate">{item.program.title}</h3>
                          {isComplete && (
                            <span className="text-xs px-2.5 py-0.5 rounded-full bg-success-50 text-green-700 font-medium flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              Complete
                            </span>
                          )}
                        </div>
                        {item.program.description && (
                          <p className="text-sm text-chocolate-500 mb-3 line-clamp-2">{item.program.description}</p>
                        )}
                        <div className="mb-3">
                          <div className="flex items-center justify-between text-xs text-chocolate-500 mb-1">
                            <span>{item.completedActivities} of {item.totalActivities} activities completed</span>
                            <span>{pct}%</span>
                          </div>
                          <div className="w-full h-2 bg-cream-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-sage-500 rounded-full transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                        {item.currentActivityTitle && !isComplete && (
                          <p className="text-xs text-chocolate-400">
                            Next: {item.currentActivityTitle}
                          </p>
                        )}
                      </div>
                      <ChevronRight className="w-5 h-5 text-chocolate-300 flex-shrink-0" />
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
